// One alarm's detail surface (Phase UI-2). Follows the DeviceDetail anatomy:
// a detail-back link, a two-column body. Left: the live runtime state (badge,
// occurrence, what-happened, live countdowns, and the ack/silence controls) plus
// the full stored definition (severity, enabled, EVERY enabled mode and its
// per-mode thresholds — this is the UI-1 fix where a multi-mode summary only
// showed the first mode). Right: this alarm's history timeline.
//
// A CUSTOM (non-recipe) alarm sets its own numbers under reserved compiler keys,
// so "How it's set up" swaps the recipe threshold table for the signal it is
// pinned to and plain-English fires / clears / delays rows, and Edit opens the
// custom form rather than the recipe-template one. Everything else on the page
// is recipe-agnostic and unchanged.
//
// Phase 7 adds three things, all for alarms that carry a custom block and/or
// conditions:
//
//   - a LIVE EVALUATION PANEL (plan D8): what each of the alarm's signals reads
//     right now, whether each clause holds, and what the alarm would do with
//     those readings. The verdicts come from `alarmLive.ts`, which reuses the
//     builder's own functions, so the two pages can never disagree;
//   - EDIT IN PLACE for a custom alarm (`CustomAlarmEditForm`), replacing the
//     phase-5 note that said it could not be done;
//   - DUPLICATE FOR A SIBLING INSTANCE (plan D3): when the catalog shows the
//     same measurement on another engine or bank, one link opens the builder
//     prefilled with this alarm aimed at it.
//
// The stored definition still comes entirely from the shared BoatChrome alerts
// snapshot — we find it by id, and an unknown id is an honest not-found. The
// live panel adds two reads, and ONLY for the alarms that show it: the signal
// catalog (also what the edit form's picker and the sibling detection need) and
// a `telemetry/latest` read scoped to this alarm's own paths, which covers a
// path that has since dropped out of the accepted catalog.

import { describeRuntimeDisabledReason } from '@neomaris/protocol';
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';

import { api } from '../api';
import { AckSilenceButtons } from '../components/alarms/AckSilenceButtons';
import { AlarmDefinitionForm } from '../components/alarms/AlarmDefinitionForm';
import { AlarmHistoryList } from '../components/alarms/AlarmHistoryList';
import { CustomAlarmEditForm } from '../components/alarms/CustomAlarmEditForm';
import { useBoatChrome } from '../components/app/boatChrome';
import { CameraAlarmSnapshotFor } from '../components/cameras/CameraViews';
import { InfoCircleIcon, ChevronRightIcon } from '../components/app/icons';
import { initFromDef, isEmptyPatch, toPatchBody, type AlarmDraft } from '../lib/alarmDefsForm';
import {
  inlineEditorVariant,
  recipePatchErrorMessage,
  withdrawnAlarmNote,
} from '../lib/alarmInlineEditor';
import { turnOffAlarm } from '../lib/alarmApi';
import { livePanel, observationLookup, rowUnits } from '../lib/alarmLive';
import { readConditions } from '../lib/alarmSentence';
import { errorContextFor, mapPatchError, type CreateErrorView } from '../lib/customAlarmErrors';
import {
  duplicateForPath,
  initFromCustomDef,
  type CustomUnitContext,
  isEmptyPatch as isEmptyCustomPatch,
  toPatchBody as toCustomPatchBody,
  type CustomAlarmDraft,
} from '../lib/customAlarmForm';
import {
  isEmptyUnlockPatch,
  leavesRecipe,
  modesFlattenedByConversion,
  toUnlockPatchBody,
  unlockedDraft,
  type AsCustomResponse,
} from '../lib/recipeUnlock';
import {
  siblingSignals,
  signalDisplayName,
  signalLabel,
  suggestedTriggerClasses,
  type Signal,
} from '../lib/signalCatalog';
import { useSignalCatalog } from '../lib/useSignalCatalog';
import { useTelemetryLatest } from '../lib/useTelemetry';
import {
  ALERT_STATE_META,
  CONDITION_UNBOUND_NOTE,
  ackProvenance,
  alarmBindingSetup,
  alarmConditionsSetup,
  alarmSeverityLabel,
  asAlertState,
  customAlarmSetup,
  displayedTitle,
  formatCountdown,
  formatTransition,
  isAckOrSilenceState,
  isCustomDefinition,
  occurrenceLabel,
  describeDelivery,
  describeRemoval,
  perModeThresholds,
  reactivationDeadline,
  readConditionUnbound,
  readCustomSpec,
  readWireTrigger,
  resolvedPathFor,
  runtimeSnapshot,
  silenceDeadline,
  unitVocabulary,
  watchScheduleSentence,
  wireConditionPaths,
  type AlarmDefView,
  type AlarmRemovalResponse,
  type DeliveryState,
} from '../lib/alarmDisplay';
import { DELETE_PROMPT } from '../lib/alarmInlineEditor';
import { findTemplate, useAlertTemplates } from '../lib/alertTemplates';
import { getBoatPresence } from '../lib/boatPresence';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useUnits } from '../lib/useUnits';

interface JustSaved {
  kind: 'created';
  compileStatus: string | null;
  rulesetVersion: number | null;
  /** Delivery state of the create's push, or null from an older client. */
  delivery?: DeliveryState | null;
}

interface PatchResponse {
  compileStatus: string | null;
  rulesetVersion: number | null;
  delivery?: { state?: DeliveryState };
}

/** The banner shown after a save/create: honest about whether it will evaluate. */
function savedBanner(
  compileStatus: string | null,
  rulesetVersion: number | null,
  compileReason: string | null
): string {
  // A disabled alarm compiles to NOTHING (the shared compiler skips it), so the
  // green "Synced — ruleset vN" would read as "your boat now watches this" when
  // it evaluates nothing — and the version shown is stale (no recompile). Say so.
  if (compileStatus === 'disabled') {
    return 'Saved — this alarm is disabled, so it isn’t evaluating.';
  }
  if (compileStatus === 'pending') {
    return compileReason !== null && compileReason.trim().length > 0
      ? `Saved — won’t evaluate yet: ${compileReason.trim()}`
      : 'Saved — won’t evaluate yet.';
  }
  return rulesetVersion !== null ? `Synced — ruleset v${rulesetVersion}` : 'Saved.';
}

/** Whether a save outcome is a "not evaluating" state that wants attention tone. */
function isAttentionStatus(compileStatus: string | null): boolean {
  return compileStatus === 'pending' || compileStatus === 'disabled';
}

function originLabel(origin: string | null): string {
  if (origin === 'cloud') return 'Added from Neomaris';
  if (origin === 'boat') return 'Authored on your boat';
  return origin ?? 'Unknown';
}

/**
 * Every telemetry path one definition names: what it WATCHES, then the paths its
 * conditions name.
 *
 * The watched path is the compiler's own resolution (`resolvedPathFor`, ADR
 * 0170) — which is what brings the live panel to a plain recipe alarm, whose
 * definition names no path at all. A PENDING definition resolves to nothing and
 * so lands back where it was: no paths, no panel, nothing claimed about a signal
 * it is not watching.
 *
 * The condition paths come from BOTH the authored clauses (what the definition
 * says) and the compiled rule's resolved clauses (what the boat is actually
 * gating on — an `engine_running` preset names no path until the compiler binds
 * it to a concrete tachometer).
 */
function definitionPaths(def: AlarmDefView | undefined): string[] {
  if (def === undefined) return [];
  const paths: string[] = [];
  const push = (path: string): void => {
    if (path.length > 0 && !paths.includes(path)) paths.push(path);
  };
  const resolved = resolvedPathFor(def);
  if (resolved !== null) push(resolved);
  const custom = readCustomSpec(def);
  if (custom !== undefined) push(custom.path);
  for (const clause of readConditions(def.conditions)?.clauses ?? []) {
    if ('path' in clause) push(clause.path);
  }
  for (const path of wireConditionPaths(def)) push(path);
  return paths;
}

export function AlarmDetail() {
  const { boatId, boat, alerts, liveSamples, lastSampleAtMs } = useBoatChrome();
  const { alarmId = '' } = useParams<{ alarmId: string }>();
  // The account's display units (ADR 0189). One read for the whole page: the
  // read blocks below, the live panel and BOTH editors' drafts take this same
  // object, so the threshold table, the panel and the box an owner types into
  // cannot end up in three different units.
  const units = useUnits();

  const response = alerts.state.kind === 'ready' ? alerts.state.response : null;
  const row = response?.alerts.find((r) => r.definition.id === alarmId) ?? null;
  // The mirror of the boat's own "power or sail" answer (ADR 0173), or null when
  // unknown. It only reaches the editor's watch-schedule field, which is the one
  // control here that speaks about sailing.
  const vesselType = response?.vesselType ?? null;

  // The live panel, the custom editor and the sibling detection all read the
  // catalog. The panel now exists for EVERY alarm the compiler could bind (ADR
  // 0170 tells us which signal that is), not just the ones that pin their own —
  // so a plain recipe alarm does now pay for the catalog and one
  // `telemetry/latest` read. That is the trade the value line is: an alarm whose
  // page cannot say what its signal reads is the complaint this fixes. A PENDING
  // alarm still names no paths and still costs nothing.
  const livePaths = useMemo(() => definitionPaths(row?.definition), [row?.definition]);
  const wantsLive = livePaths.length > 0;
  // ...until the operator opens the editor. A recipe alarm's editor can now step
  // outside its recipe (the "Edit everything" button), and that needs the same
  // catalog — the signal picker's options, and the check that the path it opens
  // on is one the boat reports. Fetching on Edit rather than on unlock means the
  // list is already in hand by the time the button is pressed, while a recipe
  // alarm nobody edits still costs nothing.
  const [wantsCatalog, setWantsCatalog] = useState(false);
  const catalog = useSignalCatalog(wantsLive || wantsCatalog ? boatId : '');
  const latestSamples = useTelemetryLatest(boatId, livePaths);

  const runtime = row?.runtime ?? null;
  const snap = runtimeSnapshot(runtime);
  const state = asAlertState(runtime?.state);
  const silenceUntil = silenceDeadline(snap);
  const reactivationAt = reactivationDeadline(snap);
  const hasCountdown = silenceUntil !== undefined || reactivationAt !== undefined;

  // One clock: 1 Hz while a countdown is live (smooth), 30 s otherwise (keeps the
  // offline-presence gate current). Runs unconditionally so hook order is stable.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const interval = hasCountdown ? 1000 : 30_000;
    const id = window.setInterval(() => setNow(Date.now()), interval);
    return () => clearInterval(id);
  }, [hasCountdown]);

  useDocumentTitle(row ? `${row.definition.title} — Alarm` : 'Alarm — Neomaris');

  const presence = boat
    ? getBoatPresence(boat.last_contacted_at, boat.created_at, lastSampleAtMs, now)
    : 'offline';
  const live = presence === 'live' || presence === 'online';

  const navigate = useNavigate();
  const location = useLocation();
  const templatesState = useAlertTemplates();
  const template = useMemo(
    () =>
      templatesState.kind === 'ready' && row
        ? (findTemplate(templatesState.templates, row.definition.recipeId) ?? null)
        : null,
    [templatesState, row]
  );

  const [editing, setEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<AlarmDraft | null>(null);
  const [customDraft, setCustomDraft] = useState<CustomAlarmDraft | null>(null);
  // The definition the open drafts were SEEDED from. Saves diff against this,
  // never the live `def`: the snapshot refreshes underneath an open editor, and
  // a diff against the refreshed one would ship every remotely-changed,
  // locally-untouched field back with its stale value.
  const [editBaseline, setEditBaseline] = useState<AlarmDefView | null>(null);
  // A RECIPE alarm opened in the custom builder (see `recipeUnlock.ts`). The seed
  // is the alarm as it stands, kept so a save can tell an ordinary edit from one
  // that takes the alarm outside its recipe — only the second converts.
  const [unlockSeed, setUnlockSeed] = useState<CustomAlarmDraft | null>(null);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState<string | null>(null);
  const [customError, setCustomError] = useState<CreateErrorView | null>(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [savedNote, setSavedNote] = useState<{
    text: string;
    attention: boolean;
    delivery?: string | null;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // The honest just-created outcome handed over by the New-alarm flow (one-shot).
  const justSaved = (location.state as { justSaved?: JustSaved } | null)?.justSaved ?? null;

  // What a CUSTOM draft needs to read in the account's units: the settings, and
  // the DECLARED store unit of the path the alarm is pinned to. Both, or the
  // store-unit gate declines and the boxes stay metric while everything around
  // them converts — the quiet failure `CustomUnitContext` names. The catalog is
  // the only source for the second, so this is `undefined` until it lands; the
  // page starts the fetch on Edit for exactly that reason.
  const customUnitContext = useMemo((): CustomUnitContext => {
    const path = row === null ? undefined : readCustomSpec(row.definition)?.path;
    const signal = path === undefined ? undefined : catalog.signals.find((s) => s.path === path);
    return { units, ...(signal === undefined ? {} : { storeUnit: signal.unit }) };
  }, [row, catalog.signals, units]);

  function stopEditing(): void {
    setEditing(false);
    setEditDraft(null);
    setCustomDraft(null);
    setEditBaseline(null);
    setUnlockSeed(null);
    setUnlockError(null);
    setCustomError(null);
    setFormError(null);
  }

  function startEdit(def: AlarmDefView): void {
    // Start the catalog loading now, so "Edit everything" is ready when pressed.
    setWantsCatalog(true);
    setEditBaseline(def);
    const custom = initFromCustomDef(def, customUnitContext);
    if (custom !== null) {
      // A CUSTOM alarm's boxes read in the account's units too (ADR 0189 §4).
      // The draft carries the settings AND the pinned signal's declared store
      // unit — both, or the store-unit gate declines and the boxes silently stay
      // metric — plus a baseline per box, so an untouched save re-emits the
      // definition's own stored number rather than a re-conversion of the
      // rounded text on screen.
      setCustomDraft(custom);
      setEditDraft(null);
    } else {
      if (template === null) return;
      // A RECIPE draft carries the units it was built with, plus a baseline per
      // box, so its numbers convert both ways and an untouched box re-emits its
      // original stored number verbatim (ADR 0189 §4).
      setEditDraft(initFromDef(def, template, units));
      setCustomDraft(null);
    }
    setCustomError(null);
    setFormError(null);
    setEditing(true);
    setSavedNote(null);
  }

  /**
   * Open a RECIPE alarm in the custom builder, seeded with what it already does.
   *
   * The translation comes from the cloud because it needs the compiler and the
   * boat's catalog — which instance the recipe bound, what hysteresis it applies,
   * what its engine-running preset resolves to. A 409 means the alarm cannot be
   * said in custom terms at all (an anchor-drift radius rule, or a recipe that
   * will not compile here); we say so and leave the recipe editor as it was,
   * rather than opening a form that could not be saved.
   */
  async function unlockToCustom(def: AlarmDefView): Promise<void> {
    if (unlocking) return;
    setUnlocking(true);
    setUnlockError(null);
    try {
      const res = await api<AsCustomResponse>(`/v1/boats/${boatId}/alerts/${def.id}/as-custom`);
      const seed = unlockedDraft(def, res);
      if (seed === null) {
        setUnlockError(
          'This alarm has settings this page can’t show yet, so it can’t be opened for full editing.'
        );
        return;
      }
      setUnlockSeed(seed);
      setCustomDraft(seed);
      setEditDraft(null);
      setCustomError(null);
    } catch (err) {
      const e = err as { status?: number; body?: { message?: string } };
      setUnlockError(
        e.status === 409
          ? `This alarm can’t be opened for full editing: ${e.body?.message ?? 'it has no equivalent here'}.`
          : 'We couldn’t open this alarm for full editing. Please try again.'
      );
    } finally {
      setUnlocking(false);
    }
  }

  /**
   * Save the unlocked editor. Converts the alarm ONLY when the edit actually
   * left the recipe (`leavesRecipe`) — renaming it or adding a mode keeps it the
   * ready-made alarm it was.
   */
  async function saveUnlocked(
    finalDraft: CustomAlarmDraft,
    seed: CustomAlarmDraft,
    original: AlarmDefView
  ): Promise<void> {
    if (busy) return;
    const patch = toUnlockPatchBody(finalDraft, seed, original);
    if (isEmptyUnlockPatch(patch)) {
      stopEditing();
      return;
    }
    setBusy(true);
    setCustomError(null);
    try {
      const res = await api<PatchResponse>(`/v1/boats/${boatId}/alerts/${original.id}`, {
        method: 'PATCH',
        body: patch,
        idempotencyKey: crypto.randomUUID(),
      });
      alerts.refresh();
      setSavedNote({
        text: savedBanner(res.compileStatus, res.rulesetVersion, original.compileReason),
        delivery: describeDelivery(res.delivery?.state),
        attention: isAttentionStatus(res.compileStatus),
      });
      stopEditing();
    } catch (err) {
      const mapped = mapPatchError(err, errorContextFor(finalDraft));
      setCustomError(mapped);
      if (mapped.staleCatalog === true) catalog.refresh();
    } finally {
      setBusy(false);
    }
  }

  /**
   * Save a custom alarm's edit. The PATCH carries only what changed
   * (`toPatchBody`), and an unchanged draft never leaves the browser — a no-op
   * request would still bump the boat's ruleset version.
   */
  async function saveCustomEdit(
    finalDraft: CustomAlarmDraft,
    original: AlarmDefView
  ): Promise<void> {
    if (busy) return;
    const patch = toCustomPatchBody(finalDraft, original);
    if (isEmptyCustomPatch(patch)) {
      stopEditing();
      return;
    }
    setBusy(true);
    setCustomError(null);
    try {
      const res = await api<PatchResponse>(`/v1/boats/${boatId}/alerts/${original.id}`, {
        method: 'PATCH',
        body: patch,
        idempotencyKey: crypto.randomUUID(),
      });
      alerts.refresh();
      setSavedNote({
        text: savedBanner(res.compileStatus, res.rulesetVersion, original.compileReason),
        delivery: describeDelivery(res.delivery?.state),
        attention: isAttentionStatus(res.compileStatus),
      });
      stopEditing();
    } catch (err) {
      // The context is what lets a 422 naming one path land on the condition row
      // that pinned it rather than always on the trigger's picker.
      const mapped = mapPatchError(err, errorContextFor(finalDraft));
      setCustomError(mapped);
      // A 422 means the catalog on screen is out of date — refetch it so the
      // picker stops offering a signal the boat has stopped reporting.
      if (mapped.staleCatalog === true) catalog.refresh();
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit(finalDraft: AlarmDraft, original: AlarmDefView): Promise<void> {
    if (busy) return;
    const patch = toPatchBody(finalDraft, original);
    if (isEmptyPatch(patch)) {
      setEditing(false);
      setEditDraft(null);
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const res = await api<PatchResponse>(`/v1/boats/${boatId}/alerts/${original.id}`, {
        method: 'PATCH',
        body: patch,
        idempotencyKey: crypto.randomUUID(),
      });
      alerts.refresh();
      setSavedNote({
        // Carry the "why" like the create path (original.compileReason) — the
        // pending recipes' reasons are recipe-inherent and stable across an edit.
        text: savedBanner(res.compileStatus, res.rulesetVersion, original.compileReason),
        delivery: describeDelivery(res.delivery?.state),
        attention: isAttentionStatus(res.compileStatus),
      });
      setEditing(false);
      setEditDraft(null);
    } catch (err) {
      // The API's own reason when it gives one — the generic copy hid a real
      // contract mismatch behind "check the values" — and the withdrawn-recipe
      // refusal in its own words. One mapper, shared with the inline editor.
      setFormError(recipePatchErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  /**
   * Turn off an alarm whose recipe is WITHDRAWN (TOM-136) — the one change the
   * server still accepts for it besides Delete, and the page's only control
   * for it short of Delete.
   */
  async function turnOffWithdrawn(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const delivery = await turnOffAlarm(boatId, alarmId);
      alerts.refresh();
      setSavedNote({ text: 'Turned off.', delivery: describeDelivery(delivery), attention: false });
    } catch (err) {
      setFormError(recipePatchErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function doDelete(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setFormError(null);
    try {
      const res = await api<AlarmRemovalResponse>(`/v1/boats/${boatId}/alerts/${alarmId}`, {
        method: 'DELETE',
        idempotencyKey: crypto.randomUUID(),
      });
      alerts.refresh();
      const kept = describeRemoval(res);
      if (kept === null) {
        navigate(`/dashboard/boats/${boatId}/alarms`);
        return;
      }
      // Kept, turned off: the boat had not taken that yet (ADR 0213). Stay on
      // the alarm — it is still in the list, and still the owner's to remove.
      setConfirmDelete(false);
      setSavedNote({ text: kept, attention: true });
      setBusy(false);
    } catch (err) {
      // Already gone (another tab, a double tap): the list shows the truth.
      if ((err as { status?: number }).status === 404) {
        alerts.refresh();
        navigate(`/dashboard/boats/${boatId}/alarms`);
        return;
      }
      setFormError('Something went wrong removing this alarm. Please try again.');
      setBusy(false);
    }
  }

  const backLink = (
    <Link className="detail-back" to={`/dashboard/boats/${boatId}/alarms`}>
      ← Back to alarms
    </Link>
  );

  if (alerts.state.kind === 'loading') {
    return <p className="muted">Loading…</p>;
  }
  if (alerts.state.kind === 'error') {
    return (
      <section>
        {backLink}
        <ul className="form-errors">
          <li>{alerts.state.message}</li>
        </ul>
      </section>
    );
  }
  if (row === null) {
    return (
      <section data-testid="alarm-detail-not-found">
        {backLink}
        <div className="page-head">
          <h1>Alarm not found</h1>
          <p className="page-sub">
            It may have been removed, or this link is for a different boat.
          </p>
        </div>
      </section>
    );
  }

  const def = row.definition;
  const stateMeta = ALERT_STATE_META[state];
  const occurrence = occurrenceLabel(runtime?.occurrenceNumber);
  // Provenance is a current attribution only while the alarm IS acked/silenced;
  // the boat never clears acknowledgeSource, so a re-fired Active carries a stale
  // human source that must not read as "Acked from …" on the live state.
  const ackLine = isAckOrSilenceState(state)
    ? ackProvenance(snap?.acknowledgeSource, snap?.acknowledgeSourceNetworkId)
    : undefined;
  const whatHappened =
    runtime === null
      ? null
      : (snap?.whatHappened ??
        // Shared describer, never raw — `paused_by_owner` (ADR 0174) is a
        // machine token and this line is the sentence an owner reads.
        describeRuntimeDisabledReason(snap?.runtimeDisabledReason) ??
        formatTransition({
          from: runtime.payload?.from,
          to: state,
          reason: runtime.payload?.reason,
        }));
  const modeRows = perModeThresholds(def.enabledModes, def.thresholdsByMode, units);
  const compilePending = def.compileStatus === 'pending';
  const explanation = def.explanation?.trim();
  // Custom alarms: the pinned signal's own store unit. The catalog's DECLARED
  // unit wins — it is the unit the thresholds were authored in and the one the
  // boat compares against — with the live sample's own label as a fallback for a
  // path the catalog has not loaded (or does not carry). Absent ⇒ bare numbers,
  // never a guessed unit. A condition clause's path gets the same treatment.
  const customSpec = readCustomSpec(def);
  const catalogByPath = new Map<string, Signal>(catalog.signals.map((s) => [s.path, s]));
  const unitForPath = (path: string): string | null | undefined =>
    catalogByPath.get(path)?.unit ?? liveSamples?.get(path)?.unit;
  // The same unit, spelled the way an owner reads it — telemetry declares every
  // temperature family as the bare glyph `C`. Numbers never move (the glyph
  // entries are factor 1, and a rescale needs `{ rescale: true }`), so this is
  // safe on the STORE-domain numbers these two blocks print. The live panel gets
  // the raw store unit instead: it applies the vocabulary itself, and the recipe
  // arm needs the store unit to decide whether a rescale is even allowed.
  const displayUnitForPath = (path: string): string | null =>
    unitVocabulary(unitForPath(path)).unit;
  const labelForPath = (path: string): string => {
    const signal = catalogByPath.get(path);
    return signal === undefined ? signalLabel(path) : signalDisplayName(signal);
  };
  // A CUSTOM alarm's trigger numbers, in the account's units where the pinned
  // path has a category (ADR 0189). `rowUnits` makes that one decision and hands
  // back the unit to print with: the RAW store unit when the account conversion
  // fires (the formatters convert from it), and the glyph vocabulary when it
  // does not — passing the glyph through a conversion would miss the store-unit
  // gate, and passing the raw unit through no conversion would print "95 C".
  const customRowUnits =
    customSpec === undefined
      ? undefined
      : rowUnits(unitForPath(customSpec.path), customSpec.path, units);
  const customSetup = customAlarmSetup(def, {
    labelFor: labelForPath,
    unit: customRowUnits?.vocab.unit,
    unitFor: displayUnitForPath,
    units,
  });
  // A prefilled title reads rounded, as the Alarms page shows it (TOM-860).
  const shownTitle = displayedTitle(def, {
    labelFor: labelForPath,
    ...(customRowUnits?.vocab.unit == null ? {} : { unit: customRowUnits.vocab.unit }),
    units,
  });
  // The authored "Only when …" conditions (recipe preset or custom clauses), and
  // the D2 case where the compiler could not attach the engine preset at all —
  // that one is honest even when the clause list itself is unreadable.
  const conditionsSetup = alarmConditionsSetup(def, {
    unitFor: displayUnitForPath,
    labelFor: labelForPath,
  });
  const conditionUnbound = readConditionUnbound(def);
  // Which INSTANCE a pinned recipe alarm watches. Without this row the page says
  // "engine oil pressure" on a two-engine boat and lets the reader assume both.
  const bindingSetup = alarmBindingSetup(def, { labelFor: labelForPath });
  // Which modes' numbers a conversion would flatten — disclosed in the unlock
  // notice, since a custom alarm carries one set of numbers for every mode.
  const flattenedModes = modesFlattenedByConversion(def);
  // True even when the block is unreadable — a `custom:<id>` def is still not a
  // recipe, so it must never be offered the recipe-template editor.
  const isCustom = isCustomDefinition(def);

  // --- the live evaluation panel (plan D8) ----------------------------------
  // The definition read back into the builder's draft: what the panel evaluates
  // from, and what Edit opens on. `null` when this build cannot represent the
  // stored alarm — then neither is offered, rather than showing half of it.
  const asDraft = initFromCustomDef(def, customUnitContext);
  // The custom editor's picker offers the catalog and its validation checks the
  // pin against it, so opening it before the catalog is loaded would greet the
  // operator with "this boat is not reporting that signal" about the signal the
  // alarm has been watching all along.
  const catalogReady = catalog.status === 'ready' && catalog.signals.length > 0;
  // A custom alarm edits through its own form; a recipe alarm needs its
  // template. A custom definition this build cannot read back into a draft is
  // offered NEITHER — the note further down says so, rather than opening an
  // editor that would save away the part it could not read.
  // An alarm on a WITHDRAWN recipe (core ADR-216, TOM-136) gets no editor: the
  // server refuses an edit that keeps it on. The note below says why, and the
  // page offers Turn off and Delete only.
  const withdrawn = inlineEditorVariant(def) === 'withdrawn';
  const canEdit = !withdrawn && (isCustom ? asDraft !== null && catalogReady : template !== null);
  // WHICH signal this alarm watches, and WHAT it watches for — the compiler's
  // own answer, derived by the cloud from the ruleset it compiled (ADR 0170).
  // `null` for a pending definition, so nothing below claims a reading for one.
  const resolvedPath = resolvedPathFor(def);
  const wireTrigger = readWireTrigger(def);
  // The alarm's OWN signal: its custom pin where it has one, else the compiled
  // rule's input path. This is the path the runtime snapshot's value belongs to.
  const triggerPath = customSpec?.path ?? resolvedPath;
  const observationFor = observationLookup({
    live: liveSamples,
    catalog: catalogByPath,
    latest: latestSamples,
    // The runtime snapshot's value belongs to the alarm's OWN signal and to the
    // moment it last changed state — a transition of ANY kind (fire, clear,
    // acknowledge), which is why the row says "at its last state change" and
    // carries `lastTransitionAt` as its age. Never a condition's path, and never
    // "now" (see `alarmLive`).
    lastTransition:
      triggerPath === null
        ? null
        : {
            path: triggerPath,
            value: snap?.currentValue ?? snap?.lastValue,
            atIso: runtime?.lastTransitionAt ?? null,
          },
  });
  const panel = wantsLive
    ? livePanel(def, {
        draft: asDraft,
        ...(customSpec === undefined
          ? {}
          : {
              triggerLabel: labelForPath(customSpec.path),
              triggerUnit: unitForPath(customSpec.path) ?? null,
            }),
        // A RECIPE alarm's trigger row. Ignored when the custom draft above
        // already supplies one; that block is the authored truth.
        ...(customSpec === undefined && resolvedPath !== null
          ? { recipeTrigger: { path: resolvedPath, trigger: wireTrigger } }
          : {}),
        labelFor: labelForPath,
        unitFor: unitForPath,
        // The TRIGGER row only (the panel's own rule): a condition clause's
        // expectation text is composed in store units, and converting one half
        // of a clause would put two units on one line.
        //
        // AND ONLY WHILE THE FORM BELOW AGREES. This panel sits directly above
        // the custom editor, so one set point printed twice in two units is the
        // failure to avoid. The EDIT draft carries `customUnitContext` and reads
        // in the account's units, so the panel converts with it. The UNLOCK
        // draft does not: `recipeUnlock.unlockedDraft` builds it with no context
        // (its own patch builder reads the boxes raw, so giving it one would
        // author a number typed in feet as metres), and its boxes are therefore
        // in the boat's units. The gate is the DRAFT's own context rather than
        // "is a custom form open", so a draft that starts converting later needs
        // no change here.
        ...(customDraft === null || customDraft.units !== undefined ? { units } : {}),
        observationFor,
        now,
      })
    : null;

  // --- duplicate for another instance (plan D3) -----------------------------
  // Only where the catalog shows the SAME measurement on another engine or bank
  // AND that sibling can carry this alarm's trigger (a High needs a number).
  const triggerClass = asDraft?.triggerClass;
  const siblings: Signal[] =
    customSpec === undefined || triggerClass === undefined
      ? []
      : siblingSignals(customSpec.path, catalog.signals).filter((s) =>
          suggestedTriggerClasses(s.kind, s.path).includes(triggerClass)
        );

  const notice: { text: string; attention: boolean; delivery?: string | null } | null =
    savedNote ??
    (justSaved
      ? {
          text: savedBanner(justSaved.compileStatus, justSaved.rulesetVersion, def.compileReason),
          attention: isAttentionStatus(justSaved.compileStatus),
          delivery: describeDelivery(justSaved.delivery ?? undefined),
        }
      : null);

  return (
    <section data-testid="alarm-detail">
      {backLink}
      <div className="page-head page-head--with-action">
        <div className="alarm-detail-head">
          {def.severity ? (
            <span className={`alarm-sev alarm-sev--${def.severity}`}>
              {alarmSeverityLabel(def.severity)}
            </span>
          ) : null}
          <h1>{shownTitle}</h1>
        </div>
        {!editing && !confirmDelete ? (
          <div className="alarm-detail-actions">
            {canEdit ? (
              <button
                type="button"
                className="btn"
                data-testid="alarm-edit-btn"
                onClick={() => startEdit(def)}
              >
                Edit
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-danger"
              data-testid="alarm-delete-btn"
              onClick={() => {
                // The confirm shows `formError` as the DELETE's error; a stale
                // one from a failed Turn off or save must not appear under it.
                setFormError(null);
                setConfirmDelete(true);
              }}
            >
              Delete
            </button>
          </div>
        ) : null}
      </div>

      {withdrawn ? (
        <div
          className="alarm-saved-note alarm-saved-note--pending alarm-withdrawn-note"
          role="note"
          data-testid="alarm-detail-withdrawn"
        >
          <InfoCircleIcon />
          <span>{withdrawnAlarmNote(def.recipeId, def.enabled)}</span>
          {def.enabled && !confirmDelete ? (
            <button
              type="button"
              className="btn"
              disabled={busy}
              data-testid="alarm-turn-off-btn"
              onClick={() => void turnOffWithdrawn()}
            >
              {busy ? 'Turning off…' : 'Turn off'}
            </button>
          ) : null}
        </div>
      ) : null}
      {withdrawn && formError !== null && !confirmDelete ? (
        <p className="alarm-form-err" role="alert" data-testid="alarm-withdrawn-error">
          {formError}
        </p>
      ) : null}

      {/* Two independent facts, never competing for one sentence: did this
          alarm compile, and did it reach the boat. The delivery line comes
          FIRST because it is the stronger claim — an alarm the boat never
          received will not evaluate however cleanly it compiled. Folding the
          two together would have hidden the compile status behind the delivery
          status for the entire un-opted-in fleet. */}
      {notice?.delivery != null ? (
        <div
          className="alarm-saved-note alarm-saved-note--pending"
          role="status"
          data-testid="alarm-detail-delivery"
        >
          <InfoCircleIcon />
          <span>{notice.delivery}</span>
        </div>
      ) : null}
      {notice !== null ? (
        <div
          className={`alarm-saved-note${notice.attention ? ' alarm-saved-note--pending' : ''}`}
          role="status"
          data-testid="alarm-detail-saved"
        >
          {notice.attention ? <InfoCircleIcon /> : null}
          <span>{notice.text}</span>
        </div>
      ) : null}

      {confirmDelete ? (
        <div className="alarm-delete-confirm" data-testid="alarm-delete-confirm">
          <p>{DELETE_PROMPT}</p>
          <div className="alarm-form-actions">
            <button
              type="button"
              className="btn btn-danger"
              disabled={busy}
              data-testid="alarm-delete-confirm-btn"
              onClick={() => void doDelete()}
            >
              {busy ? 'Removing…' : 'Remove alarm'}
            </button>
            <button
              type="button"
              className="btn"
              disabled={busy}
              onClick={() => setConfirmDelete(false)}
            >
              Keep it
            </button>
          </div>
          {formError !== null ? (
            <p className="alarm-form-err" role="alert" data-testid="alarm-delete-error">
              {formError}
            </p>
          ) : null}
        </div>
      ) : null}

      <div className="detail-cols">
        <div>
          {/* Live runtime state. */}
          <div className="panel" data-testid="alarm-detail-state">
            <h3>Right now</h3>
            <div className="alarm-detail-stateline">
              <span
                className={`alarm-state alarm-state--${stateMeta.tone}`}
                data-testid="alarm-detail-state-badge"
              >
                {stateMeta.label}
              </span>
              {occurrence ? <span className="alarm-occ">{occurrence}</span> : null}
            </div>
            {whatHappened ? <p className="alarm-what">{whatHappened}</p> : null}
            <CameraAlarmSnapshotFor
              boatId={boatId}
              alarmId={def.id}
              path={triggerPath}
              at={runtime?.lastTransitionAt ?? null}
              title={def.title}
            />
            {runtime === null ? (
              <p className="muted">Your boat hasn’t reported this alarm yet.</p>
            ) : null}
            {silenceUntil !== undefined ? (
              <p className="alarm-timers" data-testid="alarm-detail-silence">
                Silenced {formatCountdown(silenceUntil, now)}
              </p>
            ) : null}
            {reactivationAt !== undefined ? (
              <p className="alarm-timers" data-testid="alarm-detail-reactivation">
                Reactivates {formatCountdown(reactivationAt, now)}
              </p>
            ) : null}
            {ackLine ? <p className="alarm-ack">{ackLine}</p> : null}
            <AckSilenceButtons
              boatId={boatId}
              alertId={def.id}
              state={state}
              severity={def.severity}
              boatOffline={!live}
              onActionComplete={alerts.refresh}
            />
          </div>

          {/* What the readings say (plan D8). The panel above is the BOAT's own
              state; this one is what this alarm's signals read right now and
              what that would mean. They can differ legitimately — a set delay
              has not elapsed, an alarm is acknowledged — so the two are
              separate panels rather than one blended claim. */}
          {panel !== null && panel.rows.length > 0 ? (
            <div className="panel" style={{ marginTop: 14 }} data-testid="alarm-detail-live">
              <h3>What the readings say</h3>
              <ul className="alarm-live-rows">
                {panel.rows.map((r, i) => {
                  const clauseIndex = panel.rows
                    .slice(0, i)
                    .filter((x) => x.kind === 'condition').length;
                  return (
                    <li
                      key={`${r.kind}-${r.path ?? clauseIndex}`}
                      className="alarm-live-row"
                      data-testid={
                        r.kind === 'trigger'
                          ? 'alarm-detail-live-trigger'
                          : `alarm-detail-live-clause-${clauseIndex}`
                      }
                    >
                      <span className="alarm-live-row__label">{r.label}</span>
                      <span
                        className="alarm-live-row__value"
                        {...(r.kind === 'trigger'
                          ? { 'data-testid': 'alarm-detail-live-value' }
                          : {})}
                      >
                        {r.valueText}
                      </span>
                      {r.expectation !== undefined ? (
                        <span className="alarm-live-row__want">— {r.expectation}</span>
                      ) : null}
                      <span
                        className={`alarm-chip alarm-custom-chip alarm-custom-chip--${r.chip.tone}`}
                        title={r.chip.detail}
                      >
                        {r.chip.text}
                        <span className="sr-only">. {r.chip.detail}</span>
                      </span>
                    </li>
                  );
                })}
              </ul>
              {panel.combined !== null ? (
                <p
                  className={`alarm-custom-wouldfire alarm-custom-wouldfire--${panel.combined.tone}`}
                  data-testid="alarm-detail-combined"
                >
                  {panel.combined.lead} <strong>{panel.combined.verdict}</strong>
                  {panel.combined.reason === undefined ? '.' : ` — ${panel.combined.reason}.`}
                </p>
              ) : null}
              {/* Offline is not a reason to go quiet — it is a reason to say
                  which readings these are. */}
              {!live ? (
                <p className="muted" data-testid="alarm-detail-live-offline">
                  Your boat is offline, so these are the last readings it sent — not what it is
                  seeing now.
                </p>
              ) : null}
            </div>
          ) : null}

          {/* Stored definition — or the inline editor when editing. */}
          <div className="panel" style={{ marginTop: 14 }} data-testid="alarm-detail-definition">
            <h3>{editing ? 'Edit alarm' : 'How it’s set up'}</h3>

            {editing && customDraft !== null ? (
              <>
                {/* The alarm is a recipe one being edited in the full builder.
                    Say — before the save, not after — whether this edit will
                    take it out of the library, because that is not something an
                    operator should discover from the result. */}
                {unlockSeed !== null ? (
                  <div className="honest-note" data-testid="alarm-detail-unlock-note">
                    <InfoCircleIcon />
                    {leavesRecipe(customDraft, unlockSeed) ? (
                      <span data-testid="alarm-detail-unlock-converting">
                        You’ve changed what this alarm watches, so saving makes it{' '}
                        <strong>your own alarm</strong> rather than the ready-made “
                        {template?.title ?? def.recipeId}”. It keeps its name, its history and
                        everything it has ever recorded — and “{template?.title ?? def.recipeId}”
                        becomes available to add again.
                        {/* A recipe alarm may hold different numbers per mode; a
                            custom one holds a single set. Say which modes lose
                            theirs, before the save rather than after. */}
                        {flattenedModes.length > 0 ? (
                          <>
                            {' '}
                            Your own alarms use one set of numbers for every mode, so the different
                            values you have in <strong>{flattenedModes.join(', ')}</strong> will be
                            replaced by the ones above.
                          </>
                        ) : null}
                      </span>
                    ) : (
                      <span data-testid="alarm-detail-unlock-unchanged">
                        This is still the ready-made “{template?.title ?? def.recipeId}”. Change the
                        signal, what sets it off, the numbers or the conditions and it becomes your
                        own alarm instead — we’ll say so here before you save.
                      </span>
                    )}
                  </div>
                ) : null}
                <CustomAlarmEditForm
                  draft={customDraft}
                  onDraftChange={setCustomDraft}
                  signals={catalog.signals}
                  onSave={(d) =>
                    void (unlockSeed !== null
                      ? saveUnlocked(d, unlockSeed, def)
                      : saveCustomEdit(d, editBaseline ?? def))
                  }
                  onCancel={stopEditing}
                  busy={busy}
                  serverError={customError}
                  observationFor={observationFor}
                  now={now}
                  vesselType={vesselType}
                />
              </>
            ) : editing && editDraft !== null ? (
              <>
                <AlarmDefinitionForm
                  mode="edit"
                  draft={editDraft}
                  onDraftChange={setEditDraft}
                  onSave={(d) => void saveEdit(d, editBaseline ?? def)}
                  onCancel={stopEditing}
                  busy={busy}
                  serverError={formError}
                  submitLabel="Save changes"
                  pendingLabel="Saving…"
                  /* What the alarm watches TODAY, so each threshold box can show
                     that signal's reading beside the number being typed. Only
                     this page knows it — the new-alarm page has no compiled rule
                     yet and falls back to the pin the operator picks. */
                  resolvedPath={resolvedPath}
                  observationFor={observationFor}
                  now={now}
                  vesselType={vesselType}
                />
                {/* The way past the recipe's own fields. A recipe alarm could
                    only ever be edited within what its recipe exposes; changing
                    what it watches, or adding a condition the recipe cannot
                    express, meant deleting it and losing its history. */}
                <div className="alarm-unlock">
                  <p className="alarm-unlock__lead">
                    Need to change what this alarm watches, or add a condition?
                  </p>
                  {/* Held until the catalog is in hand. Opening the full editor
                      without it would greet the operator with "this boat is not
                      reporting that signal" about the signal the alarm has been
                      watching all along, and an empty picker to fix it with. */}
                  <button
                    type="button"
                    className="btn"
                    disabled={unlocking || busy || catalog.status !== 'ready'}
                    data-testid="alarm-detail-unlock"
                    onClick={() => void unlockToCustom(def)}
                  >
                    {unlocking
                      ? 'Opening…'
                      : catalog.status === 'loading'
                        ? 'Loading signals…'
                        : 'Edit everything'}
                  </button>
                  {catalog.status === 'error' ? (
                    <p className="alarm-form-err" data-testid="alarm-detail-unlock-catalog-error">
                      {catalog.error ?? 'We couldn’t load this boat’s signal list.'}
                    </p>
                  ) : null}
                  {unlockError !== null ? (
                    <p className="alarm-form-err" data-testid="alarm-detail-unlock-error">
                      {unlockError}
                    </p>
                  ) : null}
                </div>
              </>
            ) : (
              <>
                <dl className="alarm-def-grid">
                  {customSetup !== null ? (
                    <>
                      <dt className="faint">Signal</dt>
                      <dd data-testid="alarm-detail-custom-signal">
                        {customSetup.signalName}
                        <span className="mono alarm-def-path">{customSetup.path}</span>
                      </dd>
                      <dt className="faint">Fires when</dt>
                      <dd data-testid="alarm-detail-custom-fires">{customSetup.firesWhen}</dd>
                      <dt className="faint">Clears when</dt>
                      <dd data-testid="alarm-detail-custom-clears">{customSetup.clearsWhen}</dd>
                      {customSetup.delays !== undefined ? (
                        <>
                          <dt className="faint">Delays</dt>
                          <dd data-testid="alarm-detail-custom-delays">{customSetup.delays}</dd>
                        </>
                      ) : null}
                    </>
                  ) : null}
                  {/* Which instance the pin names (recipe alarms; a custom
                      alarm's own path is the Signal row above). The note comes
                      from the boat's pending reason — a hard pin never falls
                      back to a sibling, so an unbound one is worth saying. */}
                  {bindingSetup !== undefined ? (
                    <>
                      <dt className="faint">Watching</dt>
                      <dd data-testid="alarm-detail-binding">
                        {bindingSetup.label}
                        {bindingSetup.note !== undefined ? (
                          <span
                            className="alarm-chip alarm-chip--attention"
                            style={{ display: 'inline-block', marginLeft: 8 }}
                            data-testid="alarm-detail-binding-note"
                          >
                            {bindingSetup.note}
                          </span>
                        ) : null}
                        <span className="mono alarm-def-path">{bindingSetup.path}</span>
                      </dd>
                    </>
                  ) : null}
                  {/* The authored qualifier conditions — recipe preset or custom
                      clauses, same block either way. */}
                  {conditionsSetup !== null ? (
                    <>
                      <dt className="faint">Only when</dt>
                      <dd data-testid="alarm-detail-conditions">
                        {conditionsSetup.rows.map((r, i) => (
                          <div key={`${r.text}-${i}`} data-testid={`alarm-detail-condition-${i}`}>
                            {r.text}
                            {/* Authored, but NOT in force — the compiler could
                                not bind it (D2). The chip sits on THIS clause,
                                not on the block: the clauses beside it are
                                still gating the alarm. */}
                            {r.unbound === true ? (
                              <span
                                className="alarm-chip alarm-chip--attention"
                                style={{ display: 'inline-block', marginLeft: 8 }}
                                data-testid="alarm-detail-conditions-unbound-chip"
                              >
                                Not attached
                              </span>
                            ) : null}
                            {r.path !== undefined ? (
                              <span className="mono alarm-def-path">{r.path}</span>
                            ) : null}
                          </div>
                        ))}
                        {conditionsSetup.logicText.length > 0 ? (
                          <span
                            className="alarm-def-note"
                            data-testid="alarm-detail-conditions-logic"
                          >
                            {conditionsSetup.logicText}
                          </span>
                        ) : null}
                      </dd>
                    </>
                  ) : null}
                  <dt className="faint">Severity</dt>
                  <dd>{def.severity ? alarmSeverityLabel(def.severity) : 'Unspecified'}</dd>
                  <dt className="faint">Status</dt>
                  <dd>{def.enabled ? 'Enabled' : 'Disabled'}</dd>
                  <dt className="faint">Origin</dt>
                  <dd>{originLabel(def.origin)}</dd>
                </dl>

                {/* The watch schedule, in the SAME sentence the list row and
                    the authoring radio use — "Watches: Always". The raw
                    `enabledModes` chips alone said "Anchored, Docked, Moored"
                    while the row for that very alarm said "Watches: Only at
                    rest": one schedule, two vocabularies, and the reader has to
                    fold six modes back into the sentence they picked.

                    The chips STAY underneath, because they are the only place
                    the exact stored set is visible. They are payload-driven and
                    verbatim, so a legacy lowercase set or a mode a newer boat
                    elected shows as stored — which is what someone comes to the
                    detail page for when the sentence above surprises them. No
                    thresholds in the sentence here: the full per-mode table is
                    directly below, where the list row has no room for one. */}
                {def.enabledModes.length > 0 ? (
                  <div className="alarm-def-watch">
                    <p className="alarm-def-watch__line" data-testid="alarm-detail-watch">
                      {watchScheduleSentence(def.enabledModes)}
                    </p>
                    <div className="alarm-def-modes" data-testid="alarm-detail-modes">
                      {def.enabledModes.map((mode) => (
                        <span key={mode} className="alarm-chip">
                          {mode}
                        </span>
                      ))}
                    </div>
                  </div>
                ) : null}

                {/* The recipe threshold table. A custom alarm's numbers are
                    reserved compiler keys, already rendered in plain English
                    above — printing them again as `setpoint 26` would be noise.
                    A custom def we could NOT read still shows them: raw stored
                    values beat nothing when something is wrong. */}
                {customSetup === null && modeRows.length > 0 ? (
                  <table className="alarm-threshold-table" data-testid="alarm-detail-thresholds">
                    <tbody>
                      {modeRows.map((r) => (
                        <tr key={r.mode}>
                          <th scope="row">{r.mode}</th>
                          <td>
                            {r.thresholds.length > 0
                              ? r.thresholds.map((t) => t.text).join(', ')
                              : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : null}

                {/* The two condition disclosures, side by side when both apply.
                    The dropped preset (D2) is one CLAUSE that isn't in force;
                    any clause beside it still gates the alarm fail-closed, and
                    letting the unbound banner swallow that would hide the
                    safety fact. `failClosedNote` is absent exactly when nothing
                    is in force — then only the unbound banner shows. */}
                {conditionUnbound ? (
                  <div className="honest-note" data-testid="alarm-detail-condition-unbound">
                    <InfoCircleIcon />
                    <span>{CONDITION_UNBOUND_NOTE}</span>
                  </div>
                ) : null}
                {conditionsSetup?.failClosedNote !== undefined ? (
                  <div className="honest-note" data-testid="alarm-detail-condition-failclosed">
                    <InfoCircleIcon />
                    <span>{conditionsSetup.failClosedNote}</span>
                  </div>
                ) : null}

                {isCustom && customSetup === null ? (
                  <div className="honest-note" data-testid="alarm-detail-custom-unreadable">
                    <InfoCircleIcon />
                    This is a custom alarm, but we couldn’t read what it watches — it may have been
                    set up by a newer version of Neomaris.
                  </div>
                ) : null}

                {compilePending ? (
                  <div className="honest-note" data-testid="alarm-detail-pending">
                    <InfoCircleIcon />
                    {def.compileReason
                      ? `Not evaluating yet — ${def.compileReason}`
                      : 'Not evaluating yet.'}
                  </div>
                ) : null}

                {isCustom && asDraft === null && customSetup !== null ? (
                  <p className="muted" data-testid="alarm-detail-custom-edit-note">
                    This alarm can’t be edited here — part of it was set up by a newer version of
                    Neomaris. To change it, remove it and set it up again.
                  </p>
                ) : null}

                {/* Editing needs the boat's signal list. Say so, and offer the
                    retry, rather than leaving an Edit button that never came. */}
                {isCustom && asDraft !== null && catalog.status === 'error' ? (
                  <div className="alarm-detail-duplicate" data-testid="alarm-detail-catalog-error">
                    <p className="muted">
                      We couldn’t load this boat’s signal list, so this alarm can’t be edited right
                      now.
                    </p>
                    <button
                      type="button"
                      className="btn-open-sm"
                      onClick={catalog.refresh}
                      disabled={catalog.refreshing}
                    >
                      {catalog.refreshing ? 'Trying…' : 'Try again'}
                    </button>
                  </div>
                ) : null}

                {/* Multi-engine coverage without fan-out (plan D3): the same
                    alarm, aimed at the sibling instance, authored as its own
                    definition. One link per sibling — a boat with two engines
                    gets one, not a menu. */}
                {asDraft !== null && siblings.length > 0 ? (
                  <div className="alarm-detail-duplicate">
                    <p className="muted">Watch the same thing on another instance:</p>
                    <div className="alarm-detail-duplicate__links">
                      {siblings.map((s) => (
                        <Link
                          key={s.path}
                          className="btn"
                          to={`/dashboard/boats/${boatId}/alarms/new/custom`}
                          state={{ customPrefill: duplicateForPath(asDraft, s.path) }}
                          data-testid={`alarm-detail-duplicate-${s.path}`}
                        >
                          Duplicate for {labelForPath(s.path)}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : null}

                {explanation !== undefined && explanation.length > 0 ? (
                  <details className="section-collapse" data-testid="alarm-detail-why">
                    <summary>
                      <ChevronRightIcon />
                      Why this alarm
                    </summary>
                    <p className="alarm-what" style={{ marginTop: 8 }}>
                      {explanation}
                    </p>
                  </details>
                ) : null}
              </>
            )}
          </div>
        </div>

        {/* History timeline for this alarm. */}
        <div>
          <div className="panel">
            <h3>History</h3>
            <AlarmHistoryList boatId={boatId} alertId={def.id} testid="alarm-detail-history" />
          </div>
        </div>
      </div>
    </section>
  );
}
